import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Standard page header strip used across all branded pages.
 *
 * Visual grammar mirrors the homepage pillar tiles so every section of
 * the app reads as one designed family:
 *
 *  - 3px top stripe in the section's theme color
 *  - concentric "all-seeing eye" rings in the top-right corner (color-themed)
 *  - small mono uppercase slot label (e.g., "02 / DEEP DIVE")
 *  - BIG bold heading (text-5xl → text-7xl)
 *  - readable subtitle paragraph (zinc-300, real prose — not micro-mono)
 *  - optional right-side actions slot
 *  - integrated back affordance (history-aware)
 *
 * Props:
 *   color: string  — hex theme color for the stripe + label + rings
 *   label: ReactNode — small mono uppercase label (e.g., "DEEP DIVE")
 *   slot?: string  — optional 2-digit slot ID prefix (e.g., "02")
 *   title: ReactNode — big heading
 *   subtitle?: ReactNode — readable paragraph below title
 *   actions?: ReactNode — right-aligned content (buttons, counts)
 *   back?: string|boolean — destination for back arrow ("/" by default; pass `false` to hide)
 *   icon?: ReactNode — small icon next to the label
 *   testid?: string
 */
export default function PageHeader({
  color = "#f5f5f5",
  label,
  slot = null,
  title,
  subtitle,
  actions,
  back = "/",
  icon = null,
  testid = "page-header",
}) {
  const navigate = useNavigate();

  // Hide the global BackButton (rendered at app shell level) while a PageHeader
  // is mounted — the header has its own integrated back affordance.
  useEffect(() => {
    document.body.dataset.pageHeader = "true";
    return () => { delete document.body.dataset.pageHeader; };
  }, []);

  // Smart back: pop browser history when there's something to pop within the
  // same origin; otherwise fall back to the prop's destination.
  const onBack = () => {
    try {
      const sameOrigin = document.referrer && document.referrer.startsWith(window.location.origin);
      if (window.history.length > 2 && sameOrigin) {
        navigate(-1);
        return;
      }
    } catch (_e) { /* fall through */ }
    navigate(typeof back === "string" ? back : "/");
  };

  return (
    <header data-testid={testid} className="relative overflow-hidden border-b border-white/10 bg-[#0a0a0a]">
      {/* Theme stripe */}
      <div className="h-[3px] w-full" style={{ backgroundColor: color }} />

      {/* Concentric "all-seeing eye" rings — top-right, color-themed, decorative */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 hidden sm:block"
        style={{ width: 360, height: 360, transform: "translate(28%, -38%)" }}
      >
        <div
          className="absolute inset-0 rounded-full border opacity-40"
          style={{ borderColor: color, opacity: 0.18 }}
        />
        <div
          className="absolute rounded-full border"
          style={{
            inset: 32,
            borderColor: color,
            opacity: 0.14,
          }}
        />
        <div
          className="absolute rounded-full border"
          style={{
            inset: 70,
            borderColor: color,
            opacity: 0.10,
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            inset: 130,
            background: color,
            opacity: 0.06,
            filter: "blur(40px)",
          }}
        />
      </div>

      {/* Body — compact vertical rhythm. Min-height kept modest so short
          subtitles don't drown in empty space, but the floor still holds the
          long-subtitle pages comfortably. (Was 24rem; trimmed 2026-02 per
          user feedback that headers felt empty.) */}
      <div className="relative max-w-5xl mx-auto px-4 sm:px-8 pt-6 pb-7 min-h-[16rem]">
        {back !== false && (
          <button
            type="button"
            onClick={onBack}
            data-testid={`${testid}-back`}
            className="flex w-fit items-center gap-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 hover:text-white transition-colors mb-5"
          >
            <ArrowLeft size={12} /> Back
          </button>
        )}

        {/* Slot + label row */}
        <div
          data-testid={`${testid}-label`}
          className="font-mono text-[11px] tracking-[0.3em] mb-4 inline-flex items-center gap-2.5 uppercase"
          style={{ color }}
        >
          {slot ? (
            <>
              <span className="opacity-70">{slot}</span>
              <span className="opacity-40">/</span>
            </>
          ) : null}
          {icon}
          {label}
        </div>

        {/* Title row */}
        <div className="flex items-end justify-between flex-wrap gap-6">
          <h1
            data-testid={`${testid}-title`}
            className="font-heading font-black uppercase tracking-tighter text-white leading-[0.9] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem]"
          >
            {title}
          </h1>
          {actions ? (
            <div data-testid={`${testid}-actions`} className="flex items-center gap-2 flex-wrap shrink-0">
              {actions}
            </div>
          ) : null}
        </div>

        {/* Subtitle — readable prose, not micro-mono */}
        {subtitle ? (
          <div
            data-testid={`${testid}-subtitle`}
            className="mt-4 max-w-3xl text-zinc-300 text-sm sm:text-base leading-relaxed"
          >
            {subtitle}
          </div>
        ) : null}
      </div>
    </header>
  );
}
